import React, { useState, useRef, useEffect } from "react";
import { useToast } from "../../../context/ToastContext";
import {
  ImagePlus,
  Briefcase,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import CropModal from "../../Profile/CropModal";
import ProfessionalRegistrationLayout from "./ProfessionalRegistrationLayout";
import { MediaUploadService } from "../../../services/MediaUploadService";
import LocationSearch from "../../common/LocationSearch";


// Custom Year Picker Component
const YearPicker = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(
    Math.min(new Date().getFullYear(), 2026),
  );
  const dropdownRef = useRef(null);
  const MIN_YEAR = 1900;
  const MAX_YEAR = 2026;

  const startYear = Math.max(displayYear - 6, MIN_YEAR);
  const endYear = Math.min(displayYear + 5, MAX_YEAR);
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i,
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePrevious = () => {
    if (displayYear - 12 >= MIN_YEAR) setDisplayYear(displayYear - 12);
    else if (displayYear > MIN_YEAR + 5) setDisplayYear(MIN_YEAR + 5);
  };
  const handleNext = () => {
    const nextYear = displayYear + 12;
    if (nextYear + 5 <= MAX_YEAR) {
      setDisplayYear(nextYear);
    } else if (displayYear < MAX_YEAR - 5) {
      setDisplayYear(MAX_YEAR - 5);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-orange-500 outline-none transition-all bg-white cursor-pointer flex items-center justify-between relative"
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || "Select Year"}
        </span>
        <ChevronRight className="w-4 h-4 text-gray-600" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl p-4">
          {/* Header with navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevious}
              className="p-1 hover:bg-orange-100 rounded transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-orange-600" />
            </button>
            <span
              className="font-bold text-gray-900"
              style={{ fontFamily: "CabinetGrotesk-Bold" }}
            >
              {startYear} - {endYear}
            </span>
            <button
              type="button"
              onClick={handleNext}
              className="p-1 hover:bg-orange-100 rounded transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-orange-600" />
            </button>
          </div>

          {/* Year Grid */}
          <div className="grid grid-cols-4 gap-2">
            {years.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => {
                  onChange(year.toString());
                  setIsOpen(false);
                }}
                className={`py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                  value === year.toString()
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-900 hover:bg-orange-100"
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function PlannerBasicInfo({ onNext, onPrev, initialData = {}, isForced = false }) {
  const [form, setForm] = useState({
    professionalRole: initialData.professionalRole || "", // "planner" | "performer" | "crew"
    firstName: initialData.firstName || "",
    lastName: initialData.lastName || "",
    companyName: initialData.companyName || "",
    bio: initialData.bio || "",
    // Restore location as {id, name} if draft has locationId+locationName, else raw string
    location: initialData.locationId && initialData.locationName
      ? { id: initialData.locationId, name: initialData.locationName }
      : initialData.location || "",
    establishmentYear:
      initialData.establishmentYear || new Date().getFullYear(),
    accountType: initialData.accountType || "individual", // "individual" | "company"
    profilePicture: initialData.profilePicture || null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(() => {
    if (initialData.profilePictureUrl) return initialData.profilePictureUrl;
    if (typeof initialData.profilePicture === "string") return initialData.profilePicture;
    return null;
  });
  const [showCropModal, setShowCropModal] = useState(false);
  const preferredAccountType = useRef(initialData.accountType || "individual");
  const [selectedImageFile, setSelectedImageFile] = useState(initialData.profilePicture || null);
  const fileInputRef = useRef(null);

  // Force 'individual' account type for crew, but remember preference
  useEffect(() => {
    if (form.professionalRole === "crew" && form.accountType !== "individual") {
      setForm((prev) => ({ ...prev, accountType: "individual" }));
    } else if (form.professionalRole !== "crew" && form.accountType !== preferredAccountType.current) {
      setForm((prev) => ({ ...prev, accountType: preferredAccountType.current }));
    }
  }, [form.professionalRole]);

  // Sync state with async initialData (fetched from draft after mount)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setForm((prev) => ({
        ...prev,
        professionalRole: initialData.professionalRole || prev.professionalRole,
        firstName: initialData.firstName || prev.firstName,
        lastName: initialData.lastName || prev.lastName,
        companyName: initialData.companyName || prev.companyName,
        bio: initialData.bio || prev.bio,
        // Restore location from draft locationId + locationName (set by getDraft)
        location: initialData.locationId && initialData.locationName
          ? { id: initialData.locationId, name: initialData.locationName }
          : initialData.location || prev.location,
        establishmentYear: initialData.establishmentYear || prev.establishmentYear,
        accountType: initialData.accountType || prev.accountType,
        profilePicture: initialData.profilePicture || prev.profilePicture,
      }));
      preferredAccountType.current = initialData.accountType || preferredAccountType.current;
      if (initialData.profilePictureUrl) {
        setImagePreview(initialData.profilePictureUrl);
      } else if (typeof initialData.profilePicture === "string") {
        setImagePreview(initialData.profilePicture);
      }
    }
  }, [initialData]);

  // Validation
  const { showError } = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageUpload = (file) => {
    if (!file) return;

    const fileExt = file.name.split('.').pop().toLowerCase();
    const fileType = file.type || '';

    // HEIC check
    if (fileExt === 'heic' || fileExt === 'heif' || fileType.includes('heic') || fileType.includes('heif')) {
      showError(`${file.name} is in HEIC format. .heic format not supported.`);
      return;
    }

    const isImage = fileType.startsWith('image/') || ['jpg', 'jpeg', 'png'].includes(fileExt);
    if (!isImage || !['jpg', 'jpeg', 'png'].includes(fileExt)) {
      showError("Please upload image under these formats: jpg, jpeg, and png.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showError(`${file.name} is too large. Maximum image size is 10MB.`);
      return;
    }
    setSelectedImageFile(file);
    setShowCropModal(true);
  };

  const handleCropSave = async (croppedFile) => {
    const previewUrl = URL.createObjectURL(croppedFile);
    setImagePreview(previewUrl);
    setForm((prev) => ({ ...prev, profilePicture: croppedFile }));
    setErrors((prev) => ({ ...prev, profilePicture: "" }));
    setShowCropModal(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.professionalRole) newErrors.professionalRole = "Please select a profession type to continue.";
    if (!form.profilePicture) newErrors.profilePicture = "Profile picture is required.";
    if (form.accountType === "individual" && !form.firstName.trim())
      newErrors.firstName = "First Name is required.";
    if (form.accountType === "individual" && !form.lastName.trim())
      newErrors.lastName = "Last Name is required.";
    if (form.accountType === "company" && !form.companyName.trim())
      newErrors.companyName = "Company Name is required.";
    if (!form.location || (typeof form.location === 'object' && !form.location.id) || (typeof form.location === 'string' && !form.location.trim())) 
      newErrors.location = "Location (City) is required.";
    if (!form.establishmentYear || Number(form.establishmentYear) < 1900)
      newErrors.establishmentYear = "Establishment Year is required.";
    const wordCount = form.bio.trim() ? form.bio.trim().split(/\s+/).length : 0;
    if (!form.bio.trim() || wordCount < 20)
      newErrors.bio = `Bio must be at least 20 words (currently ${wordCount} words).`;
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      return showError(firstError);
    }

    setLoading(true);
    try {
      // Pass the media ID string back
      await onNext({
        ...form,
        profilePictureUrl: imagePreview,
      });
    } catch (error) {
      showError(error.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ProfessionalRegistrationLayout
        title={form.accountType === "company" ? "Tell Me About Your Business" : "Tell Me About Yourself"}
        description="Help us understand your services so we can connect you with the right clients."
        currentRole={form.professionalRole}
        roleError={errors.professionalRole}
        onRoleChange={(role) => {
          setErrors((prev) => ({ ...prev, professionalRole: "" }));
          setForm((p) => ({ ...p, professionalRole: role }));
        }}
        isRoleFrozen={isForced}
        onBack={onPrev ? () => onPrev({ ...form, profilePictureUrl: imagePreview }) : undefined}
        onContinue={handleSubmit}
        isLoading={loading}
      >
        {/* Account type toggle pill — only for planner and performer, but keep space for crew */}
        <div className={`flex items-center gap-3 mb-4 ${form.professionalRole === "crew" ? "invisible" : ""}`}>
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              setForm(p => {
                const newType = p.accountType === "individual" ? "company" : "individual";
                preferredAccountType.current = newType;
                return { ...p, accountType: newType };
              });
            }}
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none border-2 ${
              form.accountType === "company" ? "border-[#FF4C18]" : "border-black"
            } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span
              className={`absolute top-[2px] left-[2px] w-[16px] h-[16px] rounded-full transition-transform duration-200 ${
                form.accountType === "company" 
                  ? "bg-[#FF4C18] translate-x-5" 
                  : "bg-black translate-x-0"
              }`}
            />
          </button>
          <span
            className={`text-sm font-semibold transition-colors duration-200 ${
              form.accountType === "company" ? "text-[#FF4C18]" : "text-black"
            }`}
            style={{ fontFamily: "CabinetGrotesk-Bold" }}
          >
            Company
          </span>
        </div>

        {/* Profile Picture — always centered */}
        <div className="flex flex-col items-center mb-4">
          <div className={`relative group w-max mx-auto ${loading ? "pointer-events-none opacity-60" : ""}`}>
            <div
              onClick={() => {
                fileInputRef.current?.click();
              }}
              className={`w-24 h-24 rounded-full bg-orange-100 flex items-center justify-center cursor-pointer overflow-hidden ${
                errors.profilePicture ? "ring-2 ring-red-500" : ""
              }`}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  className="w-full h-full object-cover"
                  alt="Profile"
                />
              ) : (
                <div className="bg-[#FFDAD4] w-full h-full group-hover:bg-orange-200 transition-colors" />
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              hidden
              disabled={loading}
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files[0])}
            />
            
            {/* Edit Icon moved outside overflow-hidden */}
            <div className="absolute bottom-0 right-0 bg-white p-1.5 rounded-full shadow border border-gray-100 pointer-events-none">
              <ImagePlus className="w-4 h-4 text-gray-600" />
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-2 text-center">
            Tap to upload or drag &amp; drop
          </p>
          <p className="text-xs font-semibold text-gray-700 mt-0.5">Profile Picture</p>
          {errors.profilePicture && (
            <p className="text-xs text-red-500 mt-0.5">{errors.profilePicture}</p>
          )}
        </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {form.accountType === "individual" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    First Name
                  </label>
                  <input
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    disabled={loading}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all ${
                      errors.firstName
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200 focus:border-orange-500"
                    } ${loading ? "bg-gray-50 text-gray-400 cursor-not-allowed" : ""}`}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Last Name
                  </label>
                  <input
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    disabled={loading}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all ${
                      errors.lastName
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200 focus:border-orange-500"
                    } ${loading ? "bg-gray-50 text-gray-400 cursor-not-allowed" : ""}`}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Company Name
                </label>
                <input
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  placeholder="Company"
                  disabled={loading}
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-all ${
                    errors.companyName
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 focus:border-orange-500"
                  } ${loading ? "bg-gray-50 text-gray-400 cursor-not-allowed" : ""}`}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LocationSearch
                label="Location"
                name="location"
                value={form.location}
                onChange={(city) =>
                  handleChange({ target: { name: "location", value: city } })
                }
                placeholder="Enter City"
                error={errors.location}
                disabled={loading}
              />
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Establishment Year
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-900 pointer-events-none z-10" />
                  <YearPicker
                    value={form.establishmentYear}
                    onChange={(year) =>
                      setForm((prev) => ({
                        ...prev,
                        establishmentYear: Number(year),
                      }))
                    }
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Professional Bio
              </label>
              <textarea
                name="bio"
                value={form.bio}
                onChange={handleChange}
                rows={3}
                disabled={loading}
                className={`w-full px-4 py-2 border rounded-lg text-sm text-gray-600 leading-relaxed outline-none resize-none transition-all ${
                  errors.bio
                    ? "border-red-500 bg-red-50"
                    : "border-gray-200 focus:border-orange-500"
                } ${loading ? "bg-gray-50 text-gray-400 cursor-not-allowed" : ""}`}
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                placeholder="Tell potential clients about your experience..."
              />
              <p className="text-[10px] text-gray-400 mt-1">
                Minimum 20 words recommended.
              </p>
            </div>
        </form>
    </ProfessionalRegistrationLayout>

      {showCropModal && selectedImageFile && (
        <CropModal
          imageFile={selectedImageFile}
          onSave={handleCropSave}
          onCancel={() => setShowCropModal(false)}
        />
      )}
    </>
  );
}

